import { FC, useState } from "react";
import { Button, Modal, useTheme } from "@monolith-forensics/monolith-ui";
import CreateClientForm from "../Forms/CreateClientForm";
import {
  GoogleQuery,
  GoogleUserList,
  GoogleWorkspaceIcon,
  IntegrationScope,
  IntegrationUser,
  M365Query,
  M365UserList,
  MicrosoftIcon,
} from "@/Features";
import ClientsAPI from "@/api/clients";

interface CreateClientModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const CreateClientModal: FC<CreateClientModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
}) => {
  const theme = useTheme();
  const [importFromM365, setImportFromM365] = useState(false);
  const [importFromGoogle, setImportFromGoogle] = useState(false);
  const { data } = M365Query.useGetIntegration({
    query: {
      scope: IntegrationScope.TENANT,
    },
    options: { enabled: show },
  });
  const { data: googleData } = GoogleQuery.useGetIntegration({
    query: {
      scope: IntegrationScope.TENANT,
    },
    options: {
      enabled: show,
    },
  });

  const handleSubmit = async () => {
    onSubmit?.();
    setImportFromM365(false);
    setImportFromGoogle(false);
    handleClose();
  };
  const handleClose = () => {
    setImportFromM365(false);
    setImportFromGoogle(false);
    onClose();
  };
  const handleCancel = () => {
    onCancel?.();
  };

  const isM365Enabled = !!data?.data[0]?.integration_id;
  const isGoogleEnabled = !!googleData?.data[0]?.integration_id;

  const checkExistingClient = async (email: string) =>
    ClientsAPI.getClients({ email });

  const createClientEntity = async (user: IntegrationUser) => {
    await ClientsAPI.createClient({
      name: user.full_name,
      title: user.title,
      email: user.email,
      organization: user.organization || "",
      office_number: user.phone || "",
      mobile_number: user.mobile || "",
      unique_id: user.id,
      notes: `Imported from ${user.type === "Google" ? "Google" : "M365"}`,
      address: user.address || "",
      city: user.city || "",
      state: user.state || "",
      zipcode: user.zip || "",
      country: user.country || "",
    });
  };

  return (
    <Modal open={show} onClose={handleClose} style={{ minWidth: 550 }}>
      {!(importFromM365 || importFromGoogle) && (
        <>
          <CreateClientForm
            width={550}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            importButton={
              isM365Enabled || isGoogleEnabled ? (
                <div style={{ display: "flex", gap: 10 }}>
                  {isM365Enabled ? (
                    <Button
                      variant="outlined"
                      size="xxs"
                      style={{
                        color: theme.palette.primary.main,
                      }}
                      onClick={() => setImportFromM365(true)}
                      leftSection={<MicrosoftIcon />}
                      title="Import from Microsoft 365 Directory"
                    >
                      Import
                    </Button>
                  ) : null}
                  {isGoogleEnabled ? (
                    <Button
                      variant="outlined"
                      size="xxs"
                      style={{
                        color: theme.palette.primary.main,
                      }}
                      onClick={() => setImportFromGoogle(true)}
                      leftSection={<GoogleWorkspaceIcon />}
                      title="Import from Google Workspace Directory"
                    >
                      Import
                    </Button>
                  ) : null}
                </div>
              ) : undefined
            }
          />
        </>
      )}
      {importFromM365 && (
        <M365UserList
          onBack={() => setImportFromM365(false)}
          onSubmit={handleSubmit}
          entityLabel="client"
          checkExisting={checkExistingClient}
          createEntity={createClientEntity}
          context={{
            scope: IntegrationScope.TENANT,
          }}
        />
      )}
      {importFromGoogle && (
        <GoogleUserList
          onBack={() => setImportFromGoogle(false)}
          onSubmit={handleSubmit}
          entityLabel="client"
          checkExisting={checkExistingClient}
          createEntity={createClientEntity}
          context={{
            scope: IntegrationScope.TENANT,
          }}
        />
      )}
    </Modal>
  );
};

export default CreateClientModal;
